import type { ReactNode } from 'react';

import Button from '@/components/public/ui/Button';
import Icon from '@/components/public/ui/Icon';
import { trackUmamiEvent } from '@/lib/analytics';

type ReceiptAction = {
  label: string;
  href?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
};

type ReceiptStep = {
  label: string;
  description: string;
};

type SubmissionReceiptProps = {
  title: string;
  message: ReactNode;
  formType: string;
  steps?: ReceiptStep[];
  actions?: ReceiptAction[];
};

export function SubmissionReceipt({
  title,
  message,
  formType,
  steps = [],
  actions = [],
}: SubmissionReceiptProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="border-tertiary/25 bg-tertiary/[0.04] relative overflow-hidden rounded-2xl border p-6 text-center md:p-8"
    >
      <div className="bg-tertiary absolute inset-x-0 top-0 h-1" aria-hidden="true" />
      <div className="bg-tertiary/10 text-tertiary mx-auto flex h-14 w-14 items-center justify-center rounded-2xl">
        <Icon name="verified_user" size="lg" />
      </div>
      <h3 className="font-headline text-on-surface mt-4 text-2xl font-semibold">{title}</h3>
      <div className="text-on-surface-light mx-auto mt-2 max-w-2xl text-sm leading-relaxed">
        {message}
      </div>

      {steps.length > 0 && (
        <div className="mt-6 grid gap-3 text-left md:grid-cols-3">
          {steps.map((step, index) => (
            <div key={step.label} className="border-line bg-background/70 rounded-xl border p-4">
              <span className="text-tertiary text-xs font-bold tracking-widest uppercase">
                {index + 1}. {step.label}
              </span>
              <p className="text-on-surface-light mt-1 text-sm leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {actions.length > 0 && (
        <div className="mt-6 flex flex-col-reverse justify-center gap-3 sm:flex-row">
          {actions.map((action) => (
            <Button
              key={action.label}
              variant={action.variant ?? 'secondary'}
              href={action.href}
              onClick={() => {
                trackUmamiEvent('submission_receipt_action', {
                  form_type: formType,
                  label: action.label,
                  target: action.href ?? 'callback',
                });
                action.onClick?.();
              }}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
